import { ReactNode } from "react";
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tile,
} from "@carbon/react";
import i18next from "../../i18n";
import { formatDate } from "../../utils";
import { ServiceToQuote, TechnicalVisit } from "../../types";
import "./servicesToQuoteCard.scss";

type ServicesToQuoteCardProps = {
  services: ServiceToQuote[];
};

const visitText = (service: ServiceToQuote) => {
  if (!service.technicalVisit || service.technicalVisit === TechnicalVisit.NOT_NEEDED) {
    return "";
  }
  const label = i18next.t(`serviceDetail.visit.${service.technicalVisit}`);
  return service.technicalVisitDate
    ? `${label} · ${formatDate(service.technicalVisitDate)}`
    : label;
};

/** A label/value pair, shown only when it has a value. */
const Row = ({ label, children }: { label: string; children: ReactNode }) =>
  children ? (
    <div className="services-to-quote__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  ) : null;

/**
 * "Servicios a cotizar" (SCRUM-115, v2 in SCRUM-117): what was asked of suppliers for each service
 * — its name with the item it is, objective, deliverables, files, and what is quoted: each line
 * with its detail and files. An older service also shows its scope, visit and requirements. Shown to the buyer, the
 * requester and the supplier; renders nothing when there is no service detail (goods, or services
 * launched before it existed), so older quotations look as they did.
 */
const ServicesToQuoteCard = ({ services }: ServicesToQuoteCardProps) => {
  if (!services?.length) return null;

  return (
    <Tile className="services-to-quote">
      <h3 className="services-to-quote__title">
        {i18next.t("serviceDetail.toQuote.title")}
      </h3>
      <p className="services-to-quote__subtitle">
        {i18next.t("serviceDetail.toQuote.subtitle")}
      </p>

      {services.map((service, index) => (
        <section
          key={service.orderId ?? `${service.serviceName}-${index}`}
          className="services-to-quote__service"
          aria-label={service.serviceName}
        >
          <h4 className="services-to-quote__name">
            {service.title || service.serviceName}
            {service.title && (
              <Tag type="warm-gray" size="sm">
                {service.serviceName.toUpperCase()}
              </Tag>
            )}
          </h4>
          <dl className="services-to-quote__details">
            <Row label={i18next.t("serviceDetail.objective")}>{service.objective}</Row>
            <Row label={i18next.t("serviceDetail.scope")}>{service.scope}</Row>
            <Row label={i18next.t("serviceDetail.deliverables")}>
              {service.deliverables}
            </Row>
            <Row label={i18next.t("serviceDetail.technicalVisit")}>
              {visitText(service)}
            </Row>
            <Row label={i18next.t("serviceDetail.toQuote.requirements")}>
              {service.requirements.length > 0 && (
                <div className="services-to-quote__chips">
                  {service.requirements.map((code) => (
                    <Tag key={code} type="blue" size="sm">
                      {i18next.t(`serviceDetail.requirement.${code}`)}
                    </Tag>
                  ))}
                </div>
              )}
            </Row>
            <Row label={i18next.t("serviceDetail.toQuote.files")}>
              {service.files.length > 0 && (
                <div className="services-to-quote__chips">
                  {service.files.map((file) => (
                    <Link
                      key={file.fileURL}
                      href={file.fileURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                    >
                      {file.name}
                    </Link>
                  ))}
                </div>
              )}
            </Row>
            <Row label={i18next.t("serviceDetail.toQuote.lines")}>
              {service.lines.length > 0 && (
                <Table size="sm" aria-label={i18next.t("serviceDetail.toQuote.lines")}>
                  <TableHead>
                    <TableRow>
                      <TableHeader>{i18next.t("serviceDetail.toQuote.line")}</TableHeader>
                      <TableHeader className="services-to-quote__num">
                        {i18next.t("serviceDetail.lineQuantity")}
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {service.lines.map((line, i) => (
                      <TableRow key={line.code ?? i}>
                        <TableCell>
                          {line.description}
                          {line.note && (
                            <p className="services-to-quote__note">{line.note}</p>
                          )}
                          {(line.files ?? []).length > 0 && (
                            <div className="services-to-quote__chips">
                              {(line.files ?? []).map((file) => (
                                <Link
                                  key={file.fileURL}
                                  href={file.fileURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="sm"
                                >
                                  {file.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="services-to-quote__num">
                          {line.quantity}{" "}
                          {i18next.t(`serviceDetail.unitShort.${line.unit}`)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Row>
          </dl>
        </section>
      ))}
    </Tile>
  );
};

export default ServicesToQuoteCard;
